import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';
import Auth from './Auth';
import supabase from '../supabase/supabase';

const { 
  FiArrowLeft, FiArrowRight, FiCheckCircle, FiTarget, 
  FiLock, FiRocket, FiZap, FiShield, FiMail, FiUserPlus,
  FiSave, FiLoader, FiLogIn, FiChevronDown, FiChevronUp,
  FiMessageCircle, FiPlus, FiTrash2, FiBookOpen, FiEdit3
} = FiIcons;

const StepDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang].detail;
  const p = translations[lang].paths;
  const steps = stepsTranslations[lang];
  const stepId = parseInt(id);
  const step = steps.find(s => s.id === stepId);

  const [hasUnlockedAccess, setHasUnlockedAccess] = useState(false);
  const [user, setUser] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [isIntroOpen, setIsIntroOpen] = useState(true);

  // Step 1 Vision State
  const [step1Vision, setStep1Vision] = useState('');
  const [isSavingStep1, setIsSavingStep1] = useState(false);
  const [isSavedStep1, setIsSavedStep1] = useState(false);

  // Step 2 Knowledge State
  const [step2Topics, setStep2Topics] = useState([]);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [activeTopicIndex, setActiveTopicIndex] = useState(null);
  const [isSavingStep2, setIsSavingStep2] = useState(false);
  const [isSavedStep2, setIsSavedStep2] = useState(false);

  // Step 3 Idea State
  const [step3Data, setStep3Data] = useState({
    problem_solved: '',
    target_group: '',
    customer_needs: '',
    suitability: '',
    product_name_slogan: ''
  });
  const [isSavingStep3, setIsSavingStep3] = useState(false);
  const [isSavedStep3, setIsSavedStep3] = useState(false);

  // Auth check
  useEffect(() => {
    const checkUser = async () => {
      setLoadingAccess(true);
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setHasUnlockedAccess(!!currentUser);
      setLoadingAccess(false);
    };

    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setHasUnlockedAccess(!!currentUser);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Load Data
  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      if (stepId === 1) {
        const { data } = await supabase.from('step_1_vision_1773100000000').select('vision_text').eq('user_id', user.id).single();
        if (data) setStep1Vision(data.vision_text);
      } else if (stepId === 2) {
        const { data } = await supabase.from('step_2_knowledge_1773196800000').select('*').eq('user_id', user.id).order('sort_order', { ascending: true });
        if (data) {
          setStep2Topics(data);
          if (data.length > 0) setActiveTopicIndex(0);
        }
      } else if (stepId === 3) {
        const { data } = await supabase.from('step_3_ideas_1772902600000').select('*').eq('user_id', user.id).single();
        if (data) {
          setStep3Data({
            problem_solved: data.problem_solved || '',
            target_group: data.target_group || '',
            customer_needs: data.customer_needs || '',
            suitability: data.suitability || '',
            product_name_slogan: data.product_name_slogan || ''
          });
        }
      }
    };
    fetchData();
  }, [stepId, user]);

  const handleStep1Save = async () => {
    if (!user) return;
    setIsSavingStep1(true);
    const { error } = await supabase.from('step_1_vision_1773100000000').upsert({ 
      user_id: user.id, 
      vision_text: step1Vision, 
      updated_at: new Date().toISOString() 
    }, { onConflict: 'user_id' });
    setIsSavingStep1(false);
    if (!error) { 
      setIsSavedStep1(true); 
      setTimeout(() => setIsSavedStep1(false), 3000); 
    }
  };

  const handleAddStep2Topic = async () => {
    if (!newTopicTitle.trim() || !user) return;
    const newTopic = {
      user_id: user.id,
      topic: newTopicTitle,
      content: '',
      sort_order: step2Topics.length
    };
    const { data, error } = await supabase.from('step_2_knowledge_1773196800000').insert(newTopic).select().single();
    if (!error && data) {
      setStep2Topics([...step2Topics, data]);
      setNewTopicTitle('');
      setActiveTopicIndex(step2Topics.length);
    }
  };

  const handleUpdateStep2TopicContent = (index, content) => {
    const updated = [...step2Topics];
    updated[index].content = content;
    setStep2Topics(updated);
  };

  const handleDeleteStep2Topic = async (id, index) => {
    const { error } = await supabase.from('step_2_knowledge_1773196800000').delete().eq('id', id);
    if (!error) {
      const filtered = step2Topics.filter((_, i) => i !== index);
      setStep2Topics(filtered);
      if (activeTopicIndex === index) setActiveTopicIndex(filtered.length > 0 ? 0 : null);
      else if (activeTopicIndex > index) setActiveTopicIndex(activeTopicIndex - 1);
    }
  };

  const handleStep2Save = async () => {
    if (!user || step2Topics.length === 0) return;
    setIsSavingStep2(true);
    const { error } = await supabase.from('step_2_knowledge_1773196800000').upsert(
      step2Topics.map(t => ({ ...t, updated_at: new Date().toISOString() }))
    );
    setIsSavingStep2(false);
    if (!error) {
      setIsSavedStep2(true);
      setTimeout(() => setIsSavedStep2(false), 3000);
    }
  };

  const handleStep3Save = async () => {
    if (!user) return;
    setIsSavingStep3(true);
    const { error } = await supabase.from('step_3_ideas_1772902600000').upsert({ 
      user_id: user.id, 
      ...step3Data, 
      updated_at: new Date().toISOString() 
    }, { onConflict: 'user_id' });
    setIsSavingStep3(false);
    if (!error) { 
      setIsSavedStep3(true); 
      setTimeout(() => setIsSavedStep3(false), 3000); 
    }
  };

  const renderLockedOverlay = () => (
    <div className="absolute inset-x-0 top-0 bottom-0 z-20 flex items-center justify-center p-6 bg-blue-900/10 backdrop-blur-[12px] rounded-[2.5rem]">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white/95 p-8 md:p-10 rounded-[2.5rem] shadow-2xl border border-blue-100 max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner"><SafeIcon icon={FiLock} className="text-3xl text-blue-600" /></div>
          <h4 className="text-2xl font-black text-gray-900 mb-3 uppercase tracking-tight">{lang === 'de' ? 'Inhalt gesperrt' : 'Content Locked'}</h4>
          <p className="text-gray-600 font-medium">{lang === 'de' ? 'Melde dich an, um sofortigen Zugriff auf alle Tools zu erhalten.' : 'Sign in to get instant access to all tools.'}</p>
        </div>
        <div className="bg-gray-50/50 p-6 rounded-[2rem] border border-gray-100 shadow-inner"><Auth embedded={true} initialIsLogin={false} /></div>
      </motion.div>
    </div>
  );

  if (!step) return null;

  const nextStepId = stepId === 10 ? null : stepId + 1;

  return (
    <div className="min-h-screen bg-gray-50 pb-0 pt-24">
      <div className="max-w-6xl mx-auto px-4 mb-20">
        <div className="flex items-center justify-between mb-8">
          <Link to={lang === 'de' ? '/schritte' : '/steps'} className="group flex items-center space-x-2 text-gray-500 hover:text-blue-600 transition-colors bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 italic">
            <SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" /><span className="text-sm font-bold">{t.back}</span>
          </Link>
          <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{t.stepOf.replace('{current}', stepId)}</span>
        </div>

        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 mb-8 shadow-sm border border-gray-100 overflow-hidden relative">
          <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none font-black text-[200px] leading-none text-blue-900">{stepId}</div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6"><SafeIcon icon={FiTarget} /><span>{t.overview}</span></div>
            <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight leading-tight">{step.title}</h1>
            {step.quote && <div className="mb-6 pl-4 border-l-4 border-blue-500"><p className="text-xl md:text-2xl font-medium text-gray-600 italic leading-relaxed">{step.quote}</p><p className="text-sm font-black text-blue-600 uppercase tracking-widest mt-2">— {step.quoteAuthor}</p></div>}
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-gray-100 relative">
              <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiTarget} className="text-blue-600" /></div>
                <span>{t.overview}</span>
              </h2>
              <div className="space-y-6">
                <p className="text-lg text-gray-700 leading-relaxed font-medium">{step.description}</p>
                <p className="text-gray-600 leading-relaxed">{step.content}</p>
              </div>
            </div>

            {/* Step 1 Vision Theory - ALWAYS VISIBLE */}
            {stepId === 1 && (
              <div className="bg-white shadow-sm border border-gray-100 rounded-[2.5rem] overflow-hidden">
                <button 
                  onClick={() => setIsIntroOpen(!isIntroOpen)}
                  className="w-full p-8 md:p-10 flex items-center justify-between text-left hover:bg-gray-50/50 transition-colors group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <SafeIcon icon={FiZap} className="text-blue-600 text-xl" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-blue-600 uppercase tracking-widest mb-1 italic">{t.step1IntroTitle}</p>
                      <h3 className="text-2xl font-black text-gray-900 tracking-tight">{t.step1DropdownTitle}</h3>
                    </div>
                  </div>
                  <div className={`w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 transition-transform duration-300 ${isIntroOpen ? 'rotate-180' : ''}`}>
                    <SafeIcon icon={FiChevronDown} />
                  </div>
                </button>
                <AnimatePresence>
                  {isIntroOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
                      <div className="px-8 md:px-10 pb-10 space-y-6">
                        <div className="h-px bg-gray-100 mb-8" />
                        {t.step1DropdownItems.map((item, index) => (
                          <div key={index} className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                            <h4 className="text-lg font-black text-gray-900 mb-2 uppercase tracking-tight flex items-center space-x-2"><span className="w-2 h-2 bg-blue-500 rounded-full" /><span>{item.title}</span></h4>
                            <p className="text-gray-600 leading-relaxed font-medium">{item.content}</p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            <div className="relative">
              <div className={`space-y-8 transition-all duration-700 ${!hasUnlockedAccess ? 'blur-xl grayscale opacity-30 select-none' : ''}`}>
                
                {/* Step 1 Tool */}
                {stepId === 1 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center space-x-3 mb-8">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                        <SafeIcon icon={FiRocket} className="text-blue-600 text-xl" />
                      </div>
                      <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step1ToolTitle}</h3>
                    </div>
                    
                    <div className="space-y-8">
                      <div className="bg-blue-50/50 p-8 rounded-3xl border border-blue-100 space-y-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0 mt-1">
                            <SafeIcon icon={FiMessageCircle} className="text-blue-600" />
                          </div>
                          <p className="text-xl font-bold text-gray-900 leading-relaxed italic">
                            {t.step1VisionQuestion}
                          </p>
                        </div>
                        
                        <div className="space-y-4">
                          <label className="block text-sm font-black text-blue-600 uppercase tracking-widest pl-1">
                            {t.step1VisionLeadIn}
                          </label>
                          <textarea 
                            value={step1Vision} 
                            onChange={(e) => setStep1Vision(e.target.value)} 
                            placeholder={t.step1Placeholder} 
                            className="w-full bg-white border-2 border-gray-100 rounded-2xl p-6 focus:border-blue-500 focus:ring-0 transition-all min-h-[250px] font-medium text-gray-700 shadow-inner text-lg leading-relaxed" 
                          />
                        </div>
                      </div>

                      <div className="pt-4 flex items-center justify-end">
                        <button 
                          onClick={handleStep1Save} 
                          disabled={isSavingStep1 || !user} 
                          className={`flex items-center space-x-2 px-10 py-4 rounded-full font-black shadow-lg transition-all transform hover:scale-105 active:scale-95 ${isSavedStep1 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                        >
                          {isSavingStep1 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep1 ? FiCheckCircle : FiSave} />}
                          <span>{isSavingStep1 ? t.step1Saving : (isSavedStep1 ? t.step1Saved : t.step1Save)}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2 Tool - Knowledge Mapping */}
                {stepId === 2 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                          <SafeIcon icon={FiBookOpen} className="text-blue-600 text-xl" />
                        </div>
                        <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step2ToolTitle}</h3>
                      </div>
                      <button 
                        onClick={handleStep2Save}
                        disabled={isSavingStep2 || step2Topics.length === 0}
                        className={`flex items-center space-x-2 px-6 py-3 rounded-full font-black transition-all ${isSavedStep2 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                      >
                        {isSavingStep2 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep2 ? FiCheckCircle : FiSave} />}
                        <span>{isSavingStep2 ? t.step2Saving : (isSavedStep2 ? t.step2Saved : t.step2SaveAll)}</span>
                      </button>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                      {/* TOC Column */}
                      <div className="md:col-span-1 border-r border-gray-100 pr-4">
                        <div className="space-y-4">
                          <div className="relative">
                            <input 
                              type="text" 
                              value={newTopicTitle}
                              onChange={(e) => setNewTopicTitle(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleAddStep2Topic()}
                              placeholder={t.step2TopicPlaceholder}
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pl-4 pr-10 text-sm font-medium focus:ring-2 focus:ring-blue-500/20 transition-all"
                            />
                            <button 
                              onClick={handleAddStep2Topic}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors"
                            >
                              <SafeIcon icon={FiPlus} />
                            </button>
                          </div>

                          <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                            {step2Topics.length === 0 ? (
                              <p className="text-sm text-gray-400 italic py-4">{t.step2Empty}</p>
                            ) : (
                              step2Topics.map((topic, index) => (
                                <div 
                                  key={topic.id || index}
                                  className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${activeTopicIndex === index ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'hover:bg-gray-50 text-gray-600'}`}
                                  onClick={() => setActiveTopicIndex(index)}
                                >
                                  <span className="text-sm font-bold truncate pr-2">{topic.topic}</span>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); handleDeleteStep2Topic(topic.id, index); }}
                                    className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 transition-all"
                                  >
                                    <SafeIcon icon={FiTrash2} className="text-xs" />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Content Column */}
                      <div className="md:col-span-2">
                        {activeTopicIndex !== null && step2Topics[activeTopicIndex] ? (
                          <motion.div 
                            key={activeTopicIndex}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="h-full flex flex-col"
                          >
                            <div className="flex items-center space-x-2 mb-4">
                              <SafeIcon icon={FiEdit3} className="text-blue-500" />
                              <h4 className="text-lg font-black text-gray-900">{step2Topics[activeTopicIndex].topic}</h4>
                            </div>
                            <textarea 
                              value={step2Topics[activeTopicIndex].content}
                              onChange={(e) => handleUpdateStep2TopicContent(activeTopicIndex, e.target.value)}
                              placeholder={t.step2ContentPlaceholder}
                              className="w-full flex-grow bg-gray-50/50 border-2 border-gray-100 rounded-2xl p-6 focus:border-blue-500 focus:ring-0 transition-all min-h-[350px] font-medium text-gray-700 shadow-inner leading-relaxed"
                            />
                          </motion.div>
                        ) : (
                          <div className="h-full min-h-[350px] flex flex-col items-center justify-center bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-200 p-8 text-center">
                            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-4 text-gray-300">
                              <SafeIcon icon={FiBookOpen} className="text-2xl" />
                            </div>
                            <h4 className="text-lg font-bold text-gray-400">{t.step2Empty}</h4>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3 Tool */}
                {stepId === 3 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center space-x-3 mb-8">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                        <SafeIcon icon={FiRocket} className="text-blue-600 text-xl" />
                      </div>
                      <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step3ToolTitle}</h3>
                    </div>
                    
                    <div className="space-y-8">
                      {/* Explicitly rendering each field to ensure visibility */}
                      <div className="space-y-3">
                        <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t.step3Question1}</label>
                        <textarea value={step3Data.problem_solved} onChange={(e) => setStep3Data({ ...step3Data, problem_solved: e.target.value })} placeholder={t.step3Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700" />
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t.step3Question2}</label>
                        <textarea value={step3Data.target_group} onChange={(e) => setStep3Data({ ...step3Data, target_group: e.target.value })} placeholder={t.step3Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700" />
                      </div>

                      {/* THIS IS THE QUESTION THAT WAS MISSING - RE-IMPLEMENTED WITH EXTRA CARE */}
                      <div className="space-y-3">
                        <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t.step3Question3 || (lang === 'de' ? 'Welches Bedürfnis (für den Kunden) erfüllt deine Idee?' : 'What need (for the customer) does your idea fulfill?')}</label>
                        <textarea 
                          value={step3Data.customer_needs} 
                          onChange={(e) => setStep3Data({ ...step3Data, customer_needs: e.target.value })} 
                          placeholder={t.step3Placeholder} 
                          className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700 shadow-sm" 
                        />
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t.step3Question4}</label>
                        <textarea value={step3Data.suitability} onChange={(e) => setStep3Data({ ...step3Data, suitability: e.target.value })} placeholder={t.step3Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700" />
                      </div>

                      <div className="space-y-3">
                        <label className="block text-sm font-black text-gray-900 uppercase tracking-widest">{t.step3Question5}</label>
                        <textarea value={step3Data.product_name_slogan} onChange={(e) => setStep3Data({ ...step3Data, product_name_slogan: e.target.value })} placeholder={t.step3Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-4 focus:border-blue-500 focus:ring-0 transition-all min-h-[100px] font-medium text-gray-700" />
                      </div>

                      <div className="pt-4 flex items-center justify-end">
                        <button onClick={handleStep3Save} disabled={isSavingStep3 || !user} className={`flex items-center space-x-2 px-10 py-4 rounded-full font-black transition-all ${isSavedStep3 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>{isSavingStep3 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep3 ? FiCheckCircle : FiSave} />}<span>{isSavingStep3 ? t.step3Saving : (isSavedStep3 ? t.step3Saved : t.step3Save)}</span></button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-8 md:p-10 text-white shadow-xl rounded-[2.5rem]">
                  <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm"><SafeIcon icon={FiCheckCircle} className="text-white text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-widest">{t.actionItems}</h3></div>
                  <div className="space-y-4">
                    {(hasUnlockedAccess ? step.actionItems : [lang === 'de' ? 'Aktionspunkt definieren' : 'Define action item', lang === 'de' ? 'Ziel festlegen' : 'Set goal']).map((item, i) => (
                      <div key={i} className="flex items-start space-x-4 p-5 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all">
                        <div className="mt-1 min-w-[24px] w-6 h-6 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center">{hasUnlockedAccess ? <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div> : <SafeIcon icon={FiLock} className="text-[10px]" />}</div>
                        <span className="font-medium text-white leading-relaxed text-base">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {!hasUnlockedAccess && renderLockedOverlay()}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white shadow-xl sticky top-24 border border-gray-800">
              <div className="flex items-center space-x-3 mb-6"><SafeIcon icon={FiTarget} className="text-blue-400 text-xl" /><h3 className="font-black uppercase tracking-widest text-sm">{t.keyFocus}</h3></div>
              <ul className="space-y-4 mb-8">
                {step.keyPoints.map((point, i) => {
                  const isLocked = !hasUnlockedAccess && i >= 2;
                  return (
                    <li key={i} className={`flex items-center space-x-3 p-4 rounded-2xl bg-white/5 border border-white/5 transition-all duration-500 ${isLocked ? 'opacity-30 blur-[1px]' : 'text-gray-300'}`}>
                      <SafeIcon icon={isLocked ? FiLock : FiArrowRight} className={`${isLocked ? 'text-gray-500' : 'text-blue-400'} text-xs flex-shrink-0`} />
                      <span className={`font-bold text-sm ${isLocked ? 'text-gray-500' : ''}`}>{isLocked ? 'Locked' : point}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="h-px bg-white/10 my-8" />
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-400"><SafeIcon icon={hasUnlockedAccess ? FiCheckCircle : FiShield} className="text-2xl" /></div>
                <h3 className="font-black text-white mb-2 text-lg">{hasUnlockedAccess ? 'Access Active' : 'Access Restricted'}</h3>
                <button 
                  onClick={() => hasUnlockedAccess ? (nextStepId && navigate(lang === 'de' ? `/schritte/${nextStepId}` : `/steps/${nextStepId}`)) : navigate(p.register)} 
                  className="w-full mt-4 bg-white text-gray-900 hover:bg-blue-50 font-black py-4 rounded-xl flex items-center justify-center space-x-2 transition-colors"
                >
                  <span>{hasUnlockedAccess ? (nextStepId ? `${t.continueTo} ${nextStepId}` : 'All complete!') : 'Register for Access'}</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDetail;